import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import ErrorNote from "src/core/errors/components/ErrorNote";
import OrderNote from "src/core/orders/components/OrderNote";
import {
  errorsApi,
  IError
} from "src/core/errors/api/errors.api";
import type { IStore } from "src/core/shared/store";
import { useOrder } from "src/core/orders/hooks";

import { IOrderNote } from "../api/orders.api";
import OrderNoteButton from "./OrderNoteButton";

interface OrderErrorsAndNotesProps {
  orderId: number;
  terminology: ITerminology;
}

const OrderErrorsAndNotes: React.ReactFCWithChildren<OrderErrorsAndNotesProps> = ({ orderId, terminology }) => {
  const [ { currentOrder }, { refreshOrder } ] = useOrder(orderId);
  const notes = (currentOrder.data && currentOrder.data.orderNotes) || [];
  const errors = (currentOrder.data && currentOrder.data.errors) || [];
  const [ activeErrors, setActiveErrors ] = React.useState<IError[]>([]);
  const [ errorsAndNotesMerged, setErrorsAndNotesMerged ] = React.useState<Array<IError | IOrderNote>>([]);
  const [ resolveOrderErrorResponse, resolveOrderErrorRequest ] = errorsApi<IError>("RESOLVE_ORDER_ERROR");
  const { currentRole } = useMappedState(({ global }: IStore) => ({ currentRole: global.role }));

  // format errors and notes to be merged if needed
  React.useEffect(() => {
    let active: IError[] = [];
    let errorsAndNotes: Array<IError | IOrderNote> = notes || [];

    _.each(errors, err => {
      if (err.status === "active") {
        active = _.concat(active, [ err ]);
      } else {
        errorsAndNotes = _.concat(errorsAndNotes, [ err ]);
      }
    });
    setActiveErrors(active);
    setErrorsAndNotesMerged(_.sortBy(errorsAndNotes, "createdAt"));
  }, [ notes ]);

  // resolve an error
  const resolveError = React.useCallback((error: IError) => {
    if (error.id && error.code === "manual-order" && error.status !== "resolved") {
      return resolveOrderErrorRequest({ id: error.id });
    }
  }, []);

  // handle error resolution
  React.useEffect(() => {
    if (resolveOrderErrorResponse.data) {
      toast.success("Error resolved!");
      refreshOrder();
    } else if (resolveOrderErrorResponse.error) {
      toast.error(`Could not resolve this order error - ${resolveOrderErrorResponse.error.message}`);
    }
  }, [ resolveOrderErrorResponse ]);

  return (
    <>
      {currentRole && (
        <Wrapper>
          <OrderNoteButton
            orderId={orderId}
            terminology={terminology}
          />

          {activeErrors && activeErrors.length > 0 && (
            <Group>
              {activeErrors.map((error, i) => {
                return (
                  <ErrorNote
                    currentRole={currentRole}
                    handleClick={() => resolveError(error)}
                    key={i}
                    error={error}
                  />
                );
              })}
            </Group>
          )}

          {errorsAndNotesMerged && errorsAndNotesMerged.length > 0 && (
            <Group>
              {errorsAndNotesMerged.map((errorOrNote, i) => {
                if (_.has(errorOrNote, "body")) {
                  return (
                    <OrderNote
                      key={i}
                      orderMode={currentOrder.mode}
                      user={(errorOrNote as IOrderNote).author}
                      note={errorOrNote as IOrderNote}
                    />
                  );
                } else {
                  return (
                    <ErrorNote
                      currentRole={currentRole}
                      handleClick={() => resolveError(errorOrNote as IError)}
                      key={i}
                      error={errorOrNote as IError}
                    />
                  );
                }
              })}
            </Group>
          )}
        </Wrapper>
      )}
    </>
  );
};

const Wrapper = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing.margins.md};
  `}
`;

const Group = styled.div``;

export default OrderErrorsAndNotes;
